import { Box, Button, Typography, type SxProps, type Theme } from "@mui/material";
import React, { useEffect, useState } from "react";
import ZoomableImage from "components/ImagesCroper/ZoomableImage";
import { getAuditReceiptFile } from "services/auditService";

interface AuditReceiptImageCardProps {
  queueId: string;
  title?: string;
  caption?: React.ReactNode;
  reviewUrl?: string;
  loadingText: string;
  containerSx?: SxProps<Theme>;
  visibilityKey?: string;
  onVisibilityChange?: (key: string, isVisible: boolean) => void;
}

export default function AuditReceiptImageCard({
  queueId,
  title,
  caption,
  reviewUrl,
  loadingText,
  containerSx,
  visibilityKey,
  onVisibilityChange,
}: AuditReceiptImageCardProps) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [isClosed, setIsClosed] = useState(false);
  const rootSx = Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : [];
  const resolvedVisibilityKey = visibilityKey ?? queueId;

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const loadImage = async () => {
      try {
        const receiptFile = await getAuditReceiptFile(queueId);

        if (!isMounted || !receiptFile?.blob) {
          return;
        }

        const contentType = String(receiptFile.contentType || receiptFile.blob.type || "").toLowerCase();
        const nextFilename = receiptFile.filename || `receipt-${queueId}`;

        objectUrl = URL.createObjectURL(receiptFile.blob);
        setFileUrl(objectUrl);
        setFilename(nextFilename);
        setIsPdf(nextFilename.toLowerCase().endsWith(".pdf") || contentType.includes("application/pdf"));
      } finally {
        if (isMounted) {
          setIsLoadingImage(false);
        }
      }
    };

    setFileUrl(null);
    setFilename(null);
    setIsPdf(false);
    setIsLoadingImage(true);
    setIsClosed(false);
    loadImage();

    return () => {
      isMounted = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [queueId]);

  useEffect(() => {
    onVisibilityChange?.(resolvedVisibilityKey, isLoadingImage || Boolean(fileUrl));
  }, [fileUrl, isLoadingImage, onVisibilityChange, resolvedVisibilityKey]);

  if (!isLoadingImage && !fileUrl) {
    return null;
  }

  if (isClosed) {
    return (
      <Box
        sx={[
          {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            minWidth: 0,
            p: 1.5,
            border: "1px solid #C8CEE3",
            borderRadius: 1,
            bgcolor: "background.paper",
          },
          ...rootSx,
        ]}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#222B45", lineHeight: 1.2 }}>
            {title || "Receipt Image"}
          </Typography>
          {caption ? (
            <Typography
              sx={{
                mt: 0.35,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.35,
                color: "#6b7280",
                wordBreak: "break-word",
              }}
            >
              {caption}
            </Typography>
          ) : null}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setIsClosed(false)}
          sx={{
            flexShrink: 0,
            minHeight: 30,
            px: 1.5,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            borderColor: "#2c6ca0",
            color: "#2c6ca0",
          }}
        >
          View Receipt
        </Button>
      </Box>
    );
  }

  if (fileUrl && isPdf) {
    return (
      <Box
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: 0,
            p: 1.5,
            border: "1px solid #C8CEE3",
            borderRadius: 1,
            bgcolor: "background.paper",
          },
          ...rootSx,
        ]}
      >
        {title ? (
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#222B45", lineHeight: 1.2 }}>
            {title}
          </Typography>
        ) : null}
        {caption ? (
          <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: "#4E5A6E", wordBreak: "break-word" }}>
            {caption}
          </Typography>
        ) : null}
        <Button
          component="a"
          href={fileUrl}
          download={filename || "receipt.pdf"}
          size="small"
          variant="outlined"
          sx={{
            alignSelf: "flex-start",
            mt: 0.5,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            borderColor: "#2c6ca0",
            color: "#2c6ca0",
          }}
        >
          Download PDF
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minWidth: 0,
          height: "1200px",
        },
        ...rootSx,
      ]}
    >
      {fileUrl && (title || caption) ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 0.6,
            minHeight: 58,
            minWidth: 0,
          }}
        >
          {title ? (
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#222B45", lineHeight: 1.2 }}>
              {title}
            </Typography>
          ) : null}

          {caption && reviewUrl ? (
            <Typography
              component="a"
              href={reviewUrl}
              target="_blank"
              rel="noreferrer"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.35,
                color: "#1976D2",
                textDecoration: "none",
                wordBreak: "break-word",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {caption}
            </Typography>
          ) : caption ? (
            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: "#4E5A6E" }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
        {fileUrl ? (
          <ZoomableImage imageUrl={fileUrl} onClose={() => setIsClosed(true)} />
        ) : isLoadingImage ? (
          <Box sx={{ p: 2, color: "#7A8497", fontSize: 13 }}>{loadingText}</Box>
        ) : null}
      </Box>
    </Box>
  );
}
